"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { CirclePlay, Clock, ChartBar } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 3
};
const wp_query = new WP_Query(wp_query_params);
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[450px] rounded-sm bg-white/5 animate-pulse" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-12">
        <p className="text-lg text-muted-foreground">No exploration kits available.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="rounded-sm bg-white/5 backdrop-blur-xl border-primary/30 shadow-lg overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_0_20px_rgba(196,138,58,0.2)] hover:border-primary group">
          <Link to={product.link} className="block relative overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full bg-muted flex items-center justify-center">
                  <CirclePlay className="w-12 h-12 text-primary/50 stroke-[1]" />
                </div>}
            </AspectRatio>
            
            {product.categories && product.categories.length > 0 && <div className="absolute top-4 left-4">
                <Badge className="bg-secondary text-white hover:bg-secondary/80 border-none shadow-sm backdrop-blur-md font-default font-medium px-3 py-1">
                  {product.categories[0].name}
                </Badge>
              </div>}
          </Link>

          <CardHeader className="pb-3 pt-6">
            <Link to={product.link}>
              <CardTitle className="text-2xl font-bold font-default line-clamp-2 group-hover:text-primary transition-colors text-foreground">
                {product.title}
              </CardTitle>
            </Link>
            {product.price !== undefined && <div className="text-xl font-semibold text-primary mt-2 font-default">
                ${product.price}
              </div>}
          </CardHeader>

          <CardContent className="flex-grow pb-4">
            {product.short_description && <div className="text-sm text-muted-foreground line-clamp-3 [&_p]:mb-0 leading-relaxed font-sans" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
            
            <div className="mt-6 grid grid-cols-2 gap-4">
               <div className="flex items-center gap-2 text-xs text-muted-foreground font-sans">
                  <Clock className="w-4 h-4 stroke-[1.5] text-primary" />
                  <span>Global Access</span>
               </div>
               <div className="flex items-center gap-2 text-xs text-muted-foreground font-sans">
                  <ChartBar className="w-4 h-4 stroke-[1.5] text-secondary" />
                  <span>Scientific Grade</span>
               </div>
            </div>

            <div className="mt-6 space-y-2">
              <div className="flex justify-between text-xs font-medium text-foreground font-sans">
                <span>Excavation Depth</span>
                <span className="text-primary">0%</span>
              </div>
              <Progress value={0} className="h-1.5 bg-white/10 [&>div]:bg-primary" />
            </div>
          </CardContent>

          <CardFooter className="pt-0 pb-6 px-6">
            <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-sm w-full py-6 text-base font-default font-semibold shadow-md hover:shadow-lg transition-all uppercase tracking-wider" text="Acquire Kit" showQuantity={false} />
          </CardFooter>
        </Card>)}
    </div>;
}
export default function MasterTheRhythm() {
  return <section data-section-id="238742" id="featuresgrid" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif tracking-widest uppercase mb-6 text-primary">
            Built to Keep You Digging Deeper
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans">
            Equip yourself with the world's most advanced soil science field kits. 
            Uncover the mysteries of the Earth's crust with Terrigma's precision tools.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3
      }}>
          <ProductsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}